import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { examService } from '../../services/examService';
import { studentService } from '../../services/studentService';
import { schoolService } from '../../services/schoolService';
import AdmitCardTemplate from '../../Components/Common/AdmitCardTemplate';
import toast from 'react-hot-toast';
import { Download, Printer, FileText, ChevronLeft } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { mockAdmitCardData } from '../../services/mockAdmitCardData'; // REMOVE THIS WHEN BACKEND IS READY


const AdmitCard = () => {
    const navigate = useNavigate();
    const [admitCards, setAdmitCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [schoolInfo, setSchoolInfo] = useState({
        name: "Pragya Sarathi School",
        tagLine: "Excellence in Education",
        logo: null,
        signatureUrl: null
    });
    const printRef = useRef();

    useEffect(() => {
        fetchSchoolData();
        fetchAdmitCards();
    }, []);

    const fetchSchoolData = async () => {
        try {
            const response = await schoolService.getSchoolDetails();
            if (response.success && response.data && response.data.length > 0) {
                const school = response.data[0]; // Only one school in system
                setSchoolInfo({
                    name: school.name || "School Name",
                    tagLine: school.tagLine || "",
                    logo: school.logoUrl || null,
                    signatureUrl: school.signatureUrl || null
                });
            }
        } catch (error) {
            console.error('Error fetching school data:', error);
            // Keep default school info if API fails
        }
    };

    const fetchAdmitCards = async () => {
        try {
            setLoading(true);
            const studentId = localStorage.getItem('selectedStudentId');

            if (!studentId) {
                toast.error('No student selected');
                navigate('/profile');
                return;
            }

            // Fetch student enrollment data
            const enrollmentResponse = await studentService.getStudentEnrollment(studentId);
            const enrollments = Array.isArray(enrollmentResponse.data)
                ? enrollmentResponse.data
                : [enrollmentResponse.data];

            // Find the enrollment where active=true and status='Enrolled'
            const activeEnrollment = enrollments.find(
                enrollment => enrollment.active === true && enrollment.status === 'Enrolled'
            );

            if (!activeEnrollment) {
                toast.error('No active enrollment found');
                setAdmitCards([]);
                setLoading(false);
                return;
            }

            // Use the enrollment _id to fetch admit card
            const enrollmentId = activeEnrollment._id;

            try {
                // Try to fetch from backend API first
                const response = await examService.getAdmitCard(enrollmentId);

                if (response.success && response.data && response.data.length > 0) {
                    setAdmitCards(response.data);
                    setSelectedCard(response.data[0]); // Select first card by default
                } else {
                    setAdmitCards([]);
                    toast.info('No admit cards available at the moment');
                }
            } catch (apiError) {
                // If API fails, use mock data for testing (REMOVE THIS IN PRODUCTION)
                console.warn('Using mock data - Backend API not available:', apiError.message);
                toast.success('🔧 Using demo data for testing (Backend not connected)', { duration: 4000 });

                setAdmitCards(mockAdmitCardData.data);
                setSelectedCard(mockAdmitCardData.data[0]);
            }
        } catch (error) {
            console.error('Error fetching admit cards:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch admit cards');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!selectedCard || !printRef.current) {
            toast.error('No admit card to download');
            return;
        }

        try {
            setDownloading(true);
            toast.loading('Preparing your admit card...');

            const element = printRef.current;
            const opt = {
                margin: 0,
                filename: `AdmitCard_${selectedCard.rollNumber}_${selectedCard.examType?.name || 'Exam'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    logging: false
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4',
                    orientation: 'portrait'
                }
            };

            await html2pdf().set(opt).from(element).save();

            toast.dismiss();
            toast.success('Admit card downloaded successfully!');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.dismiss();
            toast.error('Failed to download admit card');
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        if (!selectedCard) {
            toast.error('No admit card to print');
            return;
        }
        window.print();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading admit cards...</p>
                </div>
            </div>
        );
    }

    if (admitCards.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
                <div className="max-w-2xl mx-auto mt-20">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="mx-auto w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                            <FileText className="w-12 h-12 text-blue-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-3">No Admit Cards Available</h2>
                        <p className="text-gray-600 mb-6">
                            There are no admit cards published for you at this moment. Please check back later or contact the administration.
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
            {/* Header - Hidden on print */}
            <div className="print:hidden sticky top-0 z-10 bg-white shadow-md">
                <div className="max-w-7xl mx-auto px-3 py-2 sm:px-4 sm:py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Admit Card</h1>
                                <p className="text-xs sm:text-sm text-gray-600">
                                    {selectedCard?.examType?.name}
                                    <span className="hidden sm:inline"> - {selectedCard?.academicSession?.label}</span>
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3">
                            {/* Print Button - Hidden on Mobile */}
                            <button
                                onClick={handlePrint}
                                disabled={downloading}
                                className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                            >
                                <Printer className="w-5 h-5" />
                                Print
                            </button>

                            {/* Download Button - Smaller on Mobile */}
                            <button
                                onClick={handleDownloadPDF}
                                disabled={downloading}
                                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-5 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow sm:shadow-lg hover:shadow-md sm:hover:shadow-xl text-xs sm:text-base"
                            >
                                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                                {downloading ? '...' : <span className="sm:hidden">Download</span>}
                                {downloading ? <span className="hidden sm:inline">Downloading...</span> : <span className="hidden sm:inline">Download PDF</span>}
                            </button>
                        </div>
                    </div>

                    {/* Multiple Admit Cards Selector */}
                    {admitCards.length > 1 && (
                        <div className="mt-2 sm:mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                            {admitCards.map((card, index) => (
                                <button
                                    key={card._id}
                                    onClick={() => setSelectedCard(card)}
                                    className={`flex-shrink-0 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${selectedCard?._id === card._id
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {card.examType?.name || `Exam ${index + 1}`}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Admit Card Preview */}
            <div className="w-full sm:max-w-[210mm] mx-auto p-2 sm:p-4 print:p-0 print:max-w-[210mm]">
                <div className="print:shadow-none shadow-lg sm:shadow-2xl rounded-lg overflow-hidden bg-white">
                    <AdmitCardTemplate
                        ref={printRef}
                        data={selectedCard}
                        schoolInfo={schoolInfo}
                    />
                </div>
            </div>

            {/* Footer Note - Hidden on print */}
            <div className="print:hidden max-w-4xl mx-auto px-4 pb-8 mt-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-800 mb-2">Important Instructions:</h3>
                    <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                        <li>Download and take a printout of this admit card</li>
                        <li>Bring the admit card to all examination days</li>
                        <li>Keep your admit card safe and do not lose it</li>
                        <li>Contact the examination controller if you face any issues</li>
                    </ul>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body {
                        margin: 0;
                        padding: 0;
                    }
                    .print\\:hidden {
                        display: none !important;
                    }
                    @page {
                        size: A4;
                        margin: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdmitCard;
